import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { EligibilityCatalog, EligibilityMessage } from '../models/eligibility.models';
import { environment } from '../../environments/environment';
import { sanitizeForLog } from '@utils/functions/general';

/**
 * Servicio para gestionar el catálogo de mensajes de elegibilidad
 * Carga los mensajes desde JSON (local o remoto) y los cachea en memoria
 */
@Injectable({
  providedIn: 'root'
})
export class EligibilityCatalogService {
  
  /** Cache en memoria de catálogos por locale */
  private catalogCache = new Map<string, Observable<EligibilityCatalog>>();

  constructor(private http: HttpClient) {}

  /**
   * Carga el catálogo para un locale específico
   * Implementa cache - las siguientes llamadas retornan el Observable cacheado
   * 
   * @param locale Código de locale (ej: 'es-MX', 'en-US')
   * @returns Observable del catálogo completo
   */
  load(locale: string): Observable<EligibilityCatalog> {
    // Si ya está en cache, retornar el Observable cacheado
    if (this.catalogCache.has(locale)) {
      return this.catalogCache.get(locale)!;
    }

    // Construir URL según configuración
    const url = this.buildCatalogUrl(locale);

    // Neutralizar saltos de línea antes de interpolar en logs (CWE-117)
    const safeLocale = sanitizeForLog(locale);

    // Hacer petición HTTP y cachear el resultado
    const catalog$ = this.http.get<EligibilityCatalog>(url).pipe(
      tap((catalog) =>
        console.log(`[EligibilityCatalog] Loaded ${catalog.items.length} items for ${safeLocale}`)
      ),
      catchError(error => {
        console.error(`[EligibilityCatalog] Error loading catalog for ${safeLocale}:`, error);
        return throwError(() => new Error(`Failed to load eligibility catalog for ${safeLocale}`));
      }),
      shareReplay(1) // Compartir resultado entre múltiples suscriptores
    );

    // Guardar en cache
    this.catalogCache.set(locale, catalog$);

    return catalog$;
  }

  /**
   * Obtiene un mensaje específico por código
   * 
   * @param code Código del mensaje (ej: 'IROPS_NON_ELIGIBLE_MINUTES')
   * @param locale Código de locale
   * @returns Observable del mensaje o undefined si no se encuentra
   */
  getMessage(code: string, locale: string): Observable<EligibilityMessage | undefined> {
    
    return this.load(locale).pipe(
      map(catalog => {
        
        const message = catalog.items.find(item => item.code === code);
   
        
        return message;
      })
    );
  }

  /**
   * Construye la URL del catálogo según configuración
   * Prioridad: endpoint remoto > assets locales
   * 
   * @param locale Código de locale
   * @returns URL completa del catálogo
   */
  private buildCatalogUrl(locale: string): string {
    // Si existe endpoint en environment, usarlo
    // @ts-ignore - La propiedad podría no existir en algunos environments
    if (environment.eligibilityCatalogEndpoint) {
      // @ts-ignore
      return `${environment.eligibilityCatalogEndpoint}?locale=${locale}`;
    }
    
    // Caso contrario, usar assets locales
    return `assets/catalogs/eligibility/app20-catalog.${locale}.json`;
  }

  /**
   * Limpia el cache de catálogos
   * Útil para pruebas o recargas forzadas
   */
  clearCache(): void {
    this.catalogCache.clear();
    console.log('[EligibilityCatalog] Cache cleared');
  }
}


